import { Icon } from 'Components';
import { Review, LearningPath } from 'LearningManager/pages/QuickSetup';
import {
	AssignUsers,
	Review as CustomReview,
	SelectCourses
} from 'LearningManager/pages/CustomSetup';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import React from 'react';
import moment from 'moment';
import Organization from 'LearningManager/components/AssignUsers/Organisation';
import Property from 'LearningManager/components/AssignUsers/Property';
import Role from 'LearningManager/components/AssignUsers/Role';
import Group from 'LearningManager/components/AssignUsers/Group';
import Individual from 'LearningManager/components/AssignUsers/Individual';
import Configure from 'LearningManager/pages/Configure';

export type AssignmentSetupType = 'Quick Setup' | 'Custom Setup';
export interface Step {
	id: number;
	title: string;
	component: React.ComponentType;
}
export interface SetupProps {
	title: string;
	icon: React.ReactNode;
	selectedIcon?: React.ReactNode;
	subTitle: string;
	listTitle: string;
	list: string[];
	isRecommended?: boolean;
	isActive?: boolean;
	onClick?: () => void;
}

export interface Language {
	label: string;
}

export interface Course {
	id: string;
	number: number;
	title: string;
	subtitle: string;
	description: string;
	runTime: string;
	created: string;
	languages: Language[];
}

export interface CourseDetailModalProps {
	title: string;
	courseId: string;
	description: string;
	runTime: string;
	created: string;
	languages: Language[];
	courses: Course[];
}

export const SetupOptions: SetupProps[] = [
	{
		title: 'Quick Setup',
		icon: <Icon type="quickSetupIcon" role="img" />,
		selectedIcon: <Icon type="quickSetupFilled" role="img" />,
		subTitle: "Get started fast with Venza's proven setup",
		listTitle: 'Default assignments:',
		list: [
			'Staff - Basic Learning Path',
			'Managers - Basic + Intermediate Learning Path',
			'Leadership & IT - Basic + Intermediate + Advanced Learning Path'
		],
		isRecommended: true
	},
	{
		title: 'Custom Setup',
		icon: <Icon type="customSetupIcon" role="img" />,
		subTitle: 'Build a tailored curriculum with full control',
		listTitle: 'You can customize:',
		list: [
			'Individual courses and learning paths',
			'Properties, groups & users',
			'Status, assignments type and more'
		]
	}
];

export const quickSetupSteps: Step[] = [
	{
		id: 1,
		title: 'Learning Path Review',
		component: LearningPath
	},
	{
		id: 2,
		title: 'Configure Settings',
		component: Configure
	},
	{ id: 3, title: 'Review & Confirm', component: Review }
];

export const customSetupSteps: Step[] = [
	{ id: 1, title: 'Select Courses', component: SelectCourses },
	{ id: 2, title: 'Assign Users', component: AssignUsers },
	{
		id: 3,
		title: 'Configure Settings',
		component: Configure
	},
	{ id: 4, title: 'Review & Confirm', component: CustomReview }
];

export const getStepsByType = (type: AssignmentSetupType): Step[] =>
	type === 'Quick Setup' ? quickSetupSteps : customSetupSteps;

// Scheduled-edit flow: only Configure + Review, no course/user selection
export const getScheduledEditSteps = (type: AssignmentSetupType): Step[] => [
	{ id: 1, title: 'Configure Settings', component: Configure },
	{
		id: 2,
		title: 'Review & Confirm',
		component: type === 'Quick Setup' ? Review : CustomReview
	}
];

export const learningPaths = [
	{
		title: 'Basic Learning Path',
		duration: '20 min',
		description:
			"Learn foundational security principles—what data is, the threats it faces, and how to protect it—in a curriculum driven by the needs of today's hospitality risk landscape.",
		courses: [
			{
				id: 1,
				title: 'All About Data',
				tag: 'InfoSec',
				image: course1
			},
			{
				id: 2,
				title: 'Data Threats',
				tag: 'InfoSec',
				image: course2
			},
			{
				id: 3,
				title: 'Protecting Data',
				tag: 'InfoSec',
				image: course3
			}
		]
	},
	{
		title: 'Intermediate Learning Path',
		duration: '40 min',
		description:
			'Training that dives deeper into information security, addressing organizational-level challenges, including data breaches, policies, vendor management, and more.',
		courses: [
			{
				id: 1,
				title: 'Information Security Breach',
				tag: 'InfoSec',
				image: course4
			},
			{
				id: 2,
				title: 'Booking Hotel Scam',
				tag: 'InfoSec',
				image: course3
			},
			{
				id: 3,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course2
			},
			{
				id: 4,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course1
			}
		]
	},
	{
		title: 'Advanced Learning Path',
		duration: '15 min',
		description:
			'Training to guide company leadership on how to conceptualize and approach data protection and compliance',
		courses: [
			{
				id: 1,
				title: 'Data Security',
				tag: 'InfoSec',
				image: course4
			},
			{
				id: 2,
				title: 'Compliance',
				tag: 'InfoSec',
				image: course3
			}
		]
	}
];

export const IndividualCoursesData = [
	{
		icon: <Icon type="arrowLeft" role="img" />,
		title: 'Individual Courses',
		courses: [
			{
				id: 1,
				title: 'Phishing Simulation Training',
				courseCount: '4 courses',
				image: course1
			},
			{
				id: 2,
				title: 'Hotel Booking Scam Awareness',
				courseCount: '3 courses',
				image: course2
			},
			{
				id: 3,
				title: 'Advanced Learning Path',
				courseCount: '5 courses',
				image: course3,
				isMandatory: true
			},
			{
				id: 4,
				title: 'Information Security Breach',
				courseCount: '5 courses',
				image: course4
			},
			{
				id: 5,
				title: 'Booking Hotel Scam',
				courseCount: '5 courses',
				image: course3
			},
			{
				id: 6,
				title: 'Urgent CEO Request',
				courseCount: '5 courses',
				image: course2
			}
		]
	},
	{
		icon: <Icon type="arrowLeft" role="img" />,
		title: 'Intermediate Learning Path',
		duration: '40 min',
		description:
			'Deeper training covering breaches, policies, and organizational challenges.',
		courses: [
			{
				id: 1,
				title: 'Information Security Breach',
				tag: 'InfoSec',
				image: course4
			},
			{
				id: 2,
				title: 'Booking Hotel Scam',
				tag: 'InfoSec',
				image: course3
			},
			{
				id: 3,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course2,
				isMandatory: true
			},
			{
				id: 4,
				title: 'Urgent CEO Request',
				tag: 'InfoSec',
				image: course1
			}
		]
	}
];

export const courseDetailMockData: CourseDetailModalProps = {
	title: 'Fake Login Page',
	courseId: '1234',
	description:
		'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
	runTime: '60 minutes',
	created: '2024',
	languages: [
		{ label: 'English' },
		{ label: 'Spanish' },
		{ label: 'Chinese' },
		{ label: 'Italian' },
		{ label: 'French' },
		{ label: 'German' }
	],
	courses: [
		{
			id: '1',
			number: 1,
			title: 'Information security breaches',
			subtitle: 'Security awareness',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '2',
			number: 2,
			title: 'Information security policies',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '3',
			number: 3,
			title: 'PCI DSS Expectations for leaders',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '4',
			number: 4,
			title: 'Vulnerability and endor management',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		}
	]
};

export interface OptionProps {
	id: number;
	icon: React.ReactNode;
	selectedIcon: React.ReactNode;
	title: string;
	desc: string;
	component: React.ReactNode;
}
export interface OptionCardProps extends OptionProps {
	selectedId: number | null;
	toggleSelect: (id: number) => void;
}

// Assign Users option ids, as stored on the draft and sent as assignment scopes.
export const ASSIGN_USERS_OPTION = {
	ORGANIZATION: 214,
	PROPERTY: 35,
	ROLE: 634,
	GROUP: 676,
	INDIVIDUAL: 784
} as const;

export const options: OptionProps[] = [
	{
		id: ASSIGN_USERS_OPTION.ORGANIZATION,
		icon: <Icon type="orgOptIcon" />,
		selectedIcon: <Icon type="orgOptFilled" />,
		title: 'By Organization',
		desc: 'Send to all users in your organization',
		component: <Organization />
	},
	{
		id: ASSIGN_USERS_OPTION.PROPERTY,
		icon: <Icon type="propOptIcon" />,
		selectedIcon: <Icon type="propOptFilled" />,
		title: 'By Property',
		desc: 'Select entire properties',
		component: <Property />
	},
	{
		id: ASSIGN_USERS_OPTION.ROLE,
		icon: <Icon type="roleOptIcon" />,
		selectedIcon: <Icon type="roleOptFilled" />,
		title: 'By Role',
		desc: 'Send to all users assigned to a specific role',
		component: <Role />
	},
	{
		id: ASSIGN_USERS_OPTION.GROUP,
		icon: <Icon type="groupOptIcon" />,
		selectedIcon: <Icon type="groupOptFilled" />,
		title: 'By Group',
		desc: 'Select groups or departments',
		component: <Group />
	},
	{
		id: ASSIGN_USERS_OPTION.INDIVIDUAL,
		icon: <Icon type="individualOptIcon" />,
		selectedIcon: <Icon type="indiOptFilled" />,
		title: 'By Individual',
		desc: 'Select specific individuals by name or email',
		component: <Individual />
	}
];

export interface ConfigureState {
	assignmentName: string;
	certDuration: string;
	certExpirationDate: string;
	assignmentStart: string;
	assignmentStartDate: string;
	dueDate: string;
	dueDateValue: string;
	assignmentType: string;
}

// Whole option objects are stored (not just ids) so a selection can be shown
// anywhere without re-querying the fetched lists.
export interface SelectedGroupOption {
	id: number;
	name: string;
	label?: string;
}

export interface SelectedRoleOption {
	id: number;
	name: string;
}

export interface SelectedUserOption {
	id: string; // UUID
	firstName: string;
	lastName: string;
	email: string;
}

export type SelectedAudienceOption =
	| SelectedGroupOption
	| SelectedRoleOption
	| SelectedUserOption;

// Audience fields that hold plain option objects (users are toggled separately).
export type AssignUsersOptionField =
	| 'selectedOrganizations'
	| 'selectedProperties'
	| 'selectedRoles'
	| 'selectedGroups';

export type AssignUsersSelectionField =
	| AssignUsersOptionField
	| 'selectedIndividualUsers';

export interface CustomSetupState {
	selectedLearningPathCourseIds: number[];
	selectedIndividualCourseIds: number[];
	optionalLearningPathIds: number[];
	optionalIndividualCourseIds: number[];
	selectedAssignUsersOptionId: number | null;
	selectedOrganizations: SelectedGroupOption[];
	selectedProperties: SelectedGroupOption[];
	selectedRoles: SelectedRoleOption[];
	selectedRolePropertyIds: number[];
	selectedGroups: SelectedGroupOption[];
	selectedGroupPropertyIds: number[];
	selectedIndividualUsers: SelectedUserOption[];
	selectedIndividualPropertyIds: number[];
}

export interface QuickSetupState {
	selectedLearningPathCourseIds: number[];
}

export interface AssignmentDraft {
	configure: ConfigureState;
	quickSetup: QuickSetupState;
	customSetup: CustomSetupState;
}

// Which draft field each Assign Users option fills.
export const ASSIGN_USERS_SELECTION_FIELD: Partial<
	Record<number, AssignUsersSelectionField>
> = {
	[ASSIGN_USERS_OPTION.ORGANIZATION]: 'selectedOrganizations',
	[ASSIGN_USERS_OPTION.PROPERTY]: 'selectedProperties',
	[ASSIGN_USERS_OPTION.ROLE]: 'selectedRoles',
	[ASSIGN_USERS_OPTION.GROUP]: 'selectedGroups',
	[ASSIGN_USERS_OPTION.INDIVIDUAL]: 'selectedIndividualUsers'
};

export const DEFAULT_DRAFT: AssignmentDraft = {
	configure: {
		assignmentName: '',
		certDuration: '',
		certExpirationDate: '',
		assignmentStart: '',
		assignmentStartDate: '',
		dueDate: '',
		dueDateValue: '',
		assignmentType: ''
	},
	quickSetup: {
		selectedLearningPathCourseIds: []
	},
	customSetup: {
		selectedLearningPathCourseIds: [],
		selectedIndividualCourseIds: [],
		optionalLearningPathIds: [],
		optionalIndividualCourseIds: [],
		selectedAssignUsersOptionId: null,
		selectedOrganizations: [],
		selectedProperties: [],
		selectedRoles: [],
		selectedRolePropertyIds: [],
		selectedGroups: [],
		selectedGroupPropertyIds: [],
		selectedIndividualUsers: [],
		selectedIndividualPropertyIds: []
	}
};

// The options picked under the chosen Assign Users method.
export const getAssignUsersSelection = (
	draft: AssignmentDraft
): SelectedAudienceOption[] => {
	const field =
		ASSIGN_USERS_SELECTION_FIELD[
			draft.customSetup.selectedAssignUsersOptionId ?? -1
		];
	return field ? draft.customSetup[field] : [];
};

// Per-step completeness: what the Next button and a restored draft both go by.
// A custom certification duration isn't complete until its date is picked.
export const hasCertDuration = (draft: AssignmentDraft): boolean =>
	draft.configure.certDuration === 'custom'
		? Boolean(draft.configure.certExpirationDate)
		: Boolean(draft.configure.certDuration);

export const hasAssignmentStart = (draft: AssignmentDraft): boolean =>
	draft.configure.assignmentStart === 'schedule'
		? Boolean(draft.configure.assignmentStartDate)
		: Boolean(draft.configure.assignmentStart);

export const DUE_DATE_BEFORE_START_ERROR = 'Due date should be after start date';

export const getAssignmentStartDate = (
	configure: ConfigureState
): string | null =>
	configure.assignmentStart === 'schedule' && configure.assignmentStartDate
		? configure.assignmentStartDate
		: null;

export const CERT_EXPIRATION_BEFORE_START_ERROR =
	'Certificate expiration date should be after start date';

const isNotAfterStart = (
	date: string,
	configure: ConfigureState,
	isEditMode: boolean
): boolean => {
	const startDate =
		(isEditMode && configure.assignmentStartDate) ||
		getAssignmentStartDate(configure) ||
		moment().format('YYYY-MM-DD');
	return !moment.utc(date).isAfter(moment.utc(startDate), 'day');
};

export const isDueDateBeforeStart = (
	configure: ConfigureState,
	isEditMode = false
): boolean =>
	configure.dueDate === 'custom' &&
	Boolean(configure.dueDateValue) &&
	isNotAfterStart(configure.dueDateValue, configure, isEditMode);

// Edit mode is skipped: the stored expiry is 'MM-DD', so its year is a guess.
export const isCertExpirationBeforeStart = (
	configure: ConfigureState,
	isEditMode = false
): boolean =>
	!isEditMode &&
	configure.certDuration === 'custom' &&
	Boolean(configure.certExpirationDate) &&
	isNotAfterStart(configure.certExpirationDate, configure, isEditMode);

export const hasConfigureData = (
	draft: AssignmentDraft,
	isEditMode = false
): boolean =>
	Boolean(
		draft.configure.assignmentName.trim() &&
			hasCertDuration(draft) &&
			(isEditMode || hasAssignmentStart(draft)) &&
			draft.configure.dueDate &&
			draft.configure.assignmentType
	) &&
	!isDueDateBeforeStart(draft.configure, isEditMode) &&
	!isCertExpirationBeforeStart(draft.configure, isEditMode);

export const hasCustomSelection = (draft: AssignmentDraft): boolean =>
	draft.customSetup.selectedLearningPathCourseIds.length > 0 ||
	draft.customSetup.selectedIndividualCourseIds.length > 0;

export const hasAssignUsersSelection = (draft: AssignmentDraft): boolean =>
	getAssignUsersSelection(draft).length > 0;

// The furthest step a draft may resume at — the first step still missing data,
// indexed into that setup type's own step list.
export const getFurthestAllowedStep = (
	setupType: AssignmentSetupType,
	draft: AssignmentDraft
): number => {
	if (setupType === 'Quick Setup') {
		return hasConfigureData(draft) ? 2 : 1;
	}

	if (!hasCustomSelection(draft)) return 0;
	if (!hasAssignUsersSelection(draft)) return 1;
	if (!hasConfigureData(draft)) return 2;
	return 3;
};
